import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  PackageCheck,
  MapPin,
  User,
  CreditCard,
  Printer,
  Copy,
  Check,
  Package,
  Store,
  Repeat,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  ShieldCheck,
  Globe,
  Mail,
  Phone,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import StatusChangeModal from "./StatusChangeModal";
import ExchangeStatusModal from "./ExchangeStatusModal";

/* ------------------------------------------------------------------ */
/*  CONFIG                                                             */
/* ------------------------------------------------------------------ */
const FLOW = ["New", "Accepted", "Processing", "Packed", "Shipped", "Delivered"];
const AFTER_SALES = ["Return", "Replacement", "Repair"];

const STATUS_STYLE = {
  New: { pill: "bg-sky-100 text-sky-700", text: "text-sky-700", bar: "bg-sky-500" },
  Accepted: { pill: "bg-indigo-100 text-indigo-700", text: "text-indigo-700", bar: "bg-indigo-500" },
  Processing: { pill: "bg-amber-100 text-amber-700", text: "text-amber-700", bar: "bg-amber-500" },
  Packed: { pill: "bg-orange-100 text-orange-700", text: "text-orange-700", bar: "bg-orange-500" },
  Shipped: { pill: "bg-blue-100 text-blue-700", text: "text-blue-700", bar: "bg-blue-500" },
  Delivered: { pill: "bg-emerald-100 text-emerald-700", text: "text-emerald-700", bar: "bg-emerald-500" },
  Return: { pill: "bg-purple-100 text-purple-700", text: "text-purple-700", bar: "bg-purple-500" },
  Replacement: { pill: "bg-violet-100 text-violet-700", text: "text-violet-700", bar: "bg-violet-500" },
  Repair: { pill: "bg-teal-100 text-teal-700", text: "text-teal-700", bar: "bg-teal-500" },
  Cancelled: { pill: "bg-red-100 text-red-600", text: "text-red-600", bar: "bg-red-500" },
};

const PAYMENT_STYLE = {
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Failed: "bg-red-50 text-red-600 border-red-200",
};

const EXCHANGE_STYLE = {
  Requested: "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-600",
  Completed: "bg-blue-100 text-blue-700",
};

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "es", label: "Español" },
  { code: "zh", label: "中文" },
];

const IMG_BASE = process.env.REACT_APP_IMAGE_URL || "";

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */
const num = (v) => Number(v) || 0;

const fmtMoney = (n) =>
  `₹${num(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "--";

const fmtDateTime = (d) =>
  d
    ? `${fmtDate(d)}, ${new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
    : "--";

const shortId = (id = "") => id.slice(-6).toUpperCase();

const productImage = (p) => {
  const img = p?.images?.[0] ?? p?.image ?? p?.thumbnail;
  const url = typeof img === "string" ? img : img?.url || img?.secure_url;
  if (!url) return null;
  return /^https?:\/\//.test(url) ? url : `${IMG_BASE}${url}`;
};

/** Multilingual field → string in chosen language (falls back to English). */
const makeGetText = (lang) => (field) => {
  if (field === undefined || field === null || field === "") return "";
  if (typeof field === "object") return field[lang] || field.en || "";
  return String(field);
};

const headline = (order) => {
  switch (order.status) {
    case "Delivered":
      return `Delivered on ${fmtDate(order.updatedAt)}`;
    case "Cancelled":
      return `Cancelled on ${fmtDate(order.updatedAt)}`;
    case "New":
      return "New order - waiting for acceptance";
    default:
      return `Order ${order.status.toLowerCase()}`;
  }
};

/* ------------------------------------------------------------------ */
/*  SMALL PIECES                                                       */
/* ------------------------------------------------------------------ */
const cardCls =
  "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

const Card = ({ title, icon: Icon, right, children, className = "" }) => (
  <section className={`${cardCls} ${className}`}>
    {title && (
      <header className="flex items-center justify-between gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-200 rounded-t-xl">
        <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-800">
          {Icon && <Icon size={16} className="text-slate-500" />}
          {title}
        </h2>
        {right}
      </header>
    )}
    {children}
  </section>
);

const InfoRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm">
    <dt className="text-slate-500 shrink-0">{label}</dt>
    <dd className="text-slate-800 text-right break-words min-w-0">{children}</dd>
  </div>
);

const Tracker = ({ status }) => {
  if (status === "Cancelled") return null;
  const done = AFTER_SALES.includes(status) ? FLOW.length - 1 : FLOW.indexOf(status);
  return (
    <ol className="flex items-center w-full min-w-[460px]" aria-label="Order progress">
      {FLOW.map((step, i) => {
        const reached = i <= done;
        const current = i === done;
        return (
          <li key={step} className={`flex items-center ${i < FLOW.length - 1 ? "flex-1" : ""}`}>
            <div className="flex flex-col items-center">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${reached ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300"
                  } ${current ? "ring-4 ring-emerald-100" : ""}`}
              >
                {reached && <Check size={12} strokeWidth={3} />}
              </span>
              <span className={`mt-1.5 text-[11px] ${reached ? "text-slate-800 font-medium" : "text-slate-400"}`}>
                {step}
              </span>
            </div>
            {i < FLOW.length - 1 && (
              <span className={`flex-1 h-0.5 mx-1.5 -mt-5 ${i < done ? "bg-emerald-500" : "bg-slate-200"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
};

const Thumb = ({ product, alt }) => {
  const [failed, setFailed] = useState(false);
  const src = productImage(product);
  return (
    <div className="w-20 h-20 shrink-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
      {src && !failed ? (
        <img src={src} alt={alt} onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : (
        <Package className="text-slate-300" size={28} />
      )}
    </div>
  );
};

const PageSkeleton = () => (
  <div className="min-h-screen bg-[#F3F6FB] p-6 animate-pulse">
    <div className="w-full mx-auto space-y-5">
      <div className="h-4 w-48 bg-slate-200 rounded" />
      <div className="h-32 bg-white border border-slate-200 rounded-xl" />
      <div className="grid lg:grid-cols-[1fr_340px] gap-5">
        <div className="h-96 bg-white border border-slate-200 rounded-xl" />
        <div className="h-96 bg-white border border-slate-200 rounded-xl" />
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
const AdminOrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("en");
  const [copied, setCopied] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [exchangeModalOpen, setExchangeModalOpen] = useState(false);

  const getText = useMemo(() => makeGetText(lang), [lang]);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/orders/get-order-by-id/${id}`);
      setOrder(data.order || data);
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order details");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchOrderDetails();
  }, [id, fetchOrderDetails]);

  /* gst / extraTax / shippingCharge are stored PER UNIT, so multiply by qty */
  const summary = useMemo(() => {
    const items = order?.products || [];
    const sum = (fn) => items.reduce((a, i) => a + fn(i), 0);
    const subtotal = sum((i) => num(i.price) * num(i.quantity));
    const gst = sum((i) => num(i.gst) * num(i.quantity));
    const extraTax = sum((i) => num(i.extraTax) * num(i.quantity));
    const shipping = sum((i) => num(i.shippingCharge) * num(i.quantity));
    const computed = subtotal + gst + extraTax + shipping;
    const total = order?.totalAmount ?? computed;
    return { subtotal, gst, extraTax, shipping, total, adjustment: total - computed, units: sum((i) => num(i.quantity)) };
  }, [order]);

  const policies = useMemo(() => {
    const seen = new Set();
    const rows = [];
    (order?.products || []).forEach((it) => {
      const care = getText(it.product?.careInstruction);
      const ret = getText(it.product?.return_policy);
      const key = `${care}|${ret}`;
      if ((care || ret) && !seen.has(key)) {
        seen.add(key);
        rows.push({ name: getText(it.product?.name), care, ret });
      }
    });
    return rows;
  }, [order, getText]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(order._id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  if (loading && !order) return <PageSkeleton />;

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F3F6FB] flex flex-col items-center justify-center text-slate-600 p-6">
        <AlertCircle size={44} className="text-slate-300" />
        <p className="mt-3 text-lg font-semibold text-slate-800">Order not found</p>
        <p className="text-sm text-slate-500 mt-1">It may have been removed, or the server could not be reached.</p>
        <div className="flex gap-3 mt-5">
          <button
            onClick={fetchOrderDetails}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#b07d17] text-white hover:bg-[#8c6312]"
          >
            Try again
          </button>
          <button
            onClick={() => navigate("/admin-orders")}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-300 hover:bg-slate-50"
          >
            Back to orders
          </button>
        </div>
      </div>
    );
  }

  const style = STATUS_STYLE[order.status] || STATUS_STYLE.New;
  const addr = order.shippingAddress && typeof order.shippingAddress === "object" ? order.shippingAddress : null;
  const user = order.user && typeof order.user === "object" ? order.user : null;
  const userId = typeof order.user === "string" ? order.user : order.user?._id;
  const ex = order.exchangeRequest;
  const customerName =
    (user && `${user.firstName || ""} ${user.lastName || ""}`.trim()) || addr?.fullName || "N/A";

  return (
    <div className="min-h-screen bg-[#F3F6FB] p-4 md:p-6 font-sans">
      <div className="w-full mx-auto space-y-5">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-slate-500 print:hidden" aria-label="Breadcrumb">
          <button
            onClick={() => navigate("/admin-orders")}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:underline"
          >
            <ArrowLeft size={15} /> Orders
          </button>
          <ChevronRight size={14} />
          <span className="text-slate-700 font-medium">Order #{shortId(order._id)}</span>
        </nav>

        {/* Header card */}
        <section className={`${cardCls} overflow-hidden`}>
          <div className={`h-1 ${style.bar}`} />
          <div className="p-5 flex flex-col lg:flex-row lg:items-start justify-between gap-5">
            <div className="min-w-0">
              <h1 className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[26px] leading-8 font-extrabold text-white tracking-tight">
                <PackageCheck className="text-[#c59a3f]" size={26} />
                Order #{shortId(order._id)}
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${style.pill}`}>{order.status}</span>
                {ex?.isRequested && (
                  <span
                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full ${EXCHANGE_STYLE[ex.status] || EXCHANGE_STYLE.Requested
                      }`}
                  >
                    <Repeat size={12} /> Exchange: {ex.status}
                  </span>
                )}
              </h1>
              <p className="flex flex-wrap items-center gap-2 mt-2 text-sm text-slate-500">
                <span className="font-mono text-[13px] break-all">{order._id}</span>
                <button
                  onClick={copyId}
                  aria-label="Copy order ID"
                  className="p-1 rounded hover:bg-slate-100 text-slate-500 print:hidden"
                >
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </button>
              </p>
              <p className="mt-3 text-sm text-slate-600">
                Placed on <b className="text-slate-800 font-semibold">{fmtDateTime(order.createdAt)}</b>
                {" · "}
                {order.products?.length || 0} item{order.products?.length === 1 ? "" : "s"}
                {" · "}
                <b className="text-slate-800 font-semibold">{fmtMoney(summary.total)}</b>
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5 print:hidden">
              <button
                onClick={() => setStatusModalOpen(true)}
                className="px-5 py-2 rounded-lg text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm transition-colors"
              >
                Update status
              </button>
              {ex?.isRequested && (
                <button
                  onClick={() => setExchangeModalOpen(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
                >
                  Review exchange
                </button>
              )}
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <Printer size={15} /> Print
              </button>
              <button
                onClick={fetchOrderDetails}
                disabled={loading}
                aria-label="Refresh order"
                className="p-2 rounded-lg text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {/* Status banner + tracker */}
          <div className="px-5 pb-5">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-5 py-4">
              <p className={`text-base font-bold ${style.text}`}>{headline(order)}</p>
              {AFTER_SALES.includes(order.status) && (
                <p className="text-sm text-slate-500 mt-0.5">
                  This order is in the <b>{order.status.toLowerCase()}</b> process after delivery.
                </p>
              )}
              <div className="overflow-x-auto mt-4 pb-1">
                <Tracker status={order.status} />
              </div>
              {order.status === "Cancelled" && (
                <p className="text-sm text-slate-500 mt-1">No further fulfilment actions are possible.</p>
              )}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          {/* ---------------- LEFT ---------------- */}
          <div className="space-y-5 min-w-0">
            {/* Items */}
            <Card
              title={`Items (${order.products?.length || 0})`}
              icon={Package}
              right={
                <label className="flex items-center gap-2 text-xs text-slate-500 print:hidden">
                  <Globe size={14} />
                  <select
                    value={lang}
                    onChange={(e) => setLang(e.target.value)}
                    aria-label="Product language"
                    className="bg-white border border-slate-300 rounded-md px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50"
                  >
                    {LANGS.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </label>
              }
            >
              <div className="flex gap-4 overflow-x-auto p-5 pb-6 scrollbar-thin">
                {(order.products || []).map((item, idx) => {
                  const p = item.product;
                  const name = getText(p?.name) || "Product unavailable";
                  return (
                    <div key={item._id || idx} className="flex flex-col gap-3 p-4 w-[220px] shrink-0 border border-slate-200 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex gap-3">
                        <Thumb product={p} alt={name} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-slate-800 line-clamp-2 leading-snug">
                            {name}
                          </p>
                          {p?.sku && <p className="text-[11px] text-slate-500 mt-1">SKU: {p.sku}</p>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-200">
                        <p className="text-[12px] text-slate-500">Qty: {item.quantity}</p>
                        <p className="text-[13.5px] font-bold text-[#c45500]">
                          {fmtMoney(item.price)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Policies */}
            {policies.length > 0 && (
              <Card title="Care & return policy" icon={ShieldCheck}>
                <div className="divide-y divide-slate-100">
                  {policies.map((r, i) => (
                    <div key={i} className="px-5 py-4 text-sm">
                      {policies.length > 1 && <p className="font-semibold text-slate-800 mb-1.5">{r.name}</p>}
                      {r.care && (
                        <p className="text-slate-600">
                          <span className="font-medium text-slate-500">Care: </span>
                          {r.care}
                        </p>
                      )}
                      {r.ret && (
                        <p className="text-slate-600 mt-1">
                          <span className="font-medium text-slate-500">Returns: </span>
                          {r.ret}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* POD */}
            {order.pod_img_url && (
              <Card title="Proof of delivery" icon={PackageCheck}>
                <div className="p-5">
                  <img
                    src={order.pod_img_url}
                    alt="Proof of delivery"
                    className="w-full max-w-md rounded-lg border border-slate-200"
                  />
                  <p className="text-xs text-slate-500 mt-2">Uploaded at delivery confirmation.</p>
                </div>
              </Card>
            )}
          </div>

          {/* ---------------- RIGHT ---------------- */}
          <aside className="space-y-5 sticky top-6">
            <Card title="Price breakdown" icon={CreditCard}>
              <div className="px-5 py-4">
                <dl className="w-full text-sm space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Item subtotal ({summary.units} units)</dt>
                    <dd className="text-slate-800 font-medium">{fmtMoney(summary.subtotal)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">GST</dt>
                    <dd className="text-slate-800 font-medium">{fmtMoney(summary.gst)}</dd>
                  </div>
                  {summary.extraTax > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Extra tax</dt>
                      <dd className="text-slate-800 font-medium">{fmtMoney(summary.extraTax)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Shipping</dt>
                    <dd className="text-slate-800 font-medium">{fmtMoney(summary.shipping)}</dd>
                  </div>
                  {Math.abs(summary.adjustment) >= 0.01 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Other adjustments</dt>
                      <dd className="text-slate-800 font-medium">{fmtMoney(summary.adjustment)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 mt-2 border-t border-slate-200 text-base font-extrabold text-white">
                    <dt>Order total</dt>
                    <dd className="text-[#c45500]">{fmtMoney(summary.total)}</dd>
                  </div>
                </dl>
              </div>
            </Card>

            <Card title="Order information" icon={CreditCard}>
              <dl className="px-5 py-3 divide-y divide-slate-100">
                <InfoRow label="Order ID">
                  <span className="font-mono text-xs">{shortId(order._id)}</span>
                </InfoRow>
                <InfoRow label="Placed">{fmtDateTime(order.createdAt)}</InfoRow>
                <InfoRow label="Last updated">{fmtDateTime(order.updatedAt)}</InfoRow>
                <InfoRow label="Payment method">
                  <span className="uppercase">{order.paymentMethod || "cash"}</span>
                </InfoRow>
                <InfoRow label="Payment status">
                  <span
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PAYMENT_STYLE[order.paymentStatus] || PAYMENT_STYLE.Pending
                      }`}
                  >
                    {order.paymentStatus || "Pending"}
                  </span>
                </InfoRow>
                {order.merchantTxnNo && (
                  <InfoRow label="Transaction no.">
                    <span className="font-mono text-xs">{order.merchantTxnNo}</span>
                  </InfoRow>
                )}
              </dl>
            </Card>

            <Card title="Ship to" icon={MapPin}>
              <div className="px-5 py-4 text-sm text-slate-700 leading-6">
                {addr ? (
                  <>
                    <p className="font-semibold text-white">{addr.fullName || customerName}</p>
                    {addr.addressLine1 && <p>{addr.addressLine1}</p>}
                    {addr.addressLine2 && <p>{addr.addressLine2}</p>}
                    <p>{[addr.city, addr.state].filter(Boolean).join(", ")}</p>
                    <p>{[addr.country, addr.postalCode].filter(Boolean).join(" - ")}</p>
                    {addr.landmark && <p className="text-slate-500">Landmark: {addr.landmark}</p>}
                    {addr.phone && (
                      <p className="flex items-center gap-1.5 mt-2 text-slate-600">
                        <Phone size={13} /> {addr.phone}
                      </p>
                    )}
                    {addr.addressType && (
                      <span className="inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                        {addr.addressType}
                      </span>
                    )}
                  </>
                ) : (
                  <p className="text-slate-500">Address not available</p>
                )}
              </div>
            </Card>

            <Card title="Customer" icon={User}>
              <div className="px-5 py-4 text-sm text-slate-700 space-y-1.5">
                <p className="font-semibold text-white">{customerName}</p>
                {user?.email && (
                  <p className="flex items-center gap-1.5 text-slate-600 break-all">
                    <Mail size={13} /> {user.email}
                  </p>
                )}
                {(user?.phone || addr?.phone) && (
                  <p className="flex items-center gap-1.5 text-slate-600">
                    <Phone size={13} /> {user?.phone || addr?.phone}
                  </p>
                )}
                {userId && <p className="text-xs text-slate-400">Customer ID: {userId.slice(-8)}</p>}
              </div>
            </Card>

            {ex?.isRequested && (
              <Card
                title="Exchange request"
                icon={Repeat}
                right={
                  <span
                    className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${EXCHANGE_STYLE[ex.status] || EXCHANGE_STYLE.Requested
                      }`}
                  >
                    {ex.status}
                  </span>
                }
              >
                <div className="px-5 py-4 text-sm space-y-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">Reason</p>
                    <p className="text-slate-800 mt-0.5">{ex.reason || "No reason provided"}</p>
                  </div>
                  <p className="text-slate-600">Requested: {fmtDateTime(ex.requestedAt)}</p>
                  {ex.completedAt && <p className="text-slate-600">Completed: {fmtDateTime(ex.completedAt)}</p>}
                  <button
                    onClick={() => setExchangeModalOpen(true)}
                    className="w-full mt-1 rounded-lg py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors print:hidden"
                  >
                    Update exchange status
                  </button>
                </div>
              </Card>
            )}
          </aside>
        </div>
      </div>

      {/* Modals */}
      {statusModalOpen && (
        <StatusChangeModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          order={order}
          refreshOrders={fetchOrderDetails}
        />
      )}
      {exchangeModalOpen && ex?.isRequested && (
        <ExchangeStatusModal
          isOpen={exchangeModalOpen}
          onClose={() => setExchangeModalOpen(false)}
          order={order}
          refreshOrders={fetchOrderDetails}
        />
      )}
    </div>
  );
};

export default AdminOrderDetails;